import React from 'react';
import styled from 'astroturf/react';

import $image_url from 'js/utils/image_url';
import { currentUser } from 'js/utils/user';
import routes from 'js/core_pre_webpack/routes';
import PremiumLock from 'js/premium/PremiumLock';
import DoubleDigitCountBubble from 'js/header/FocusedNav/DoubleDigitCountBubble';
import { ImpressionWatcher } from 'js/utils/impressions/ImpressionWatcher';

const SUPPORT_LINK = 'https://support.academia.edu';
const TRIGGER = 'acct-mnu-btn';

const MenuProfilePic = styled.img`
  border-radius: 50%;
`;

const Header = styled.a`
  margin: 8px;
  padding: 12px 0;
  border-bottom: 1px solid #dddfe2;
  display: flex;
  align-items: center;
  color: #222222;
  &:hover,
  &:active,
  &:visited {
    color: #222222;
    text-decoration: none;
  }

  font-size: 12px;
  font-weight: 500;
  line-height: 17px;
  letter-spacing: 0.1em;
  text-transform: uppercase;

  > img {
    width: 20px;
    height: 20px;
    margin-right: 8px;
  }

  > div {
    // Truncate long names
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 200px;
  }
`;

const UpgradeAnchor = styled.a`
  border-radius: 4px;
  border: none;
  background: #0645b1;
  margin: 0 4px;
  padding: 6px;
  text-transform: uppercase;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  &:hover,
  &:focus,
  &:active {
    background: #0057eb;
    text-decoration: none;
    color: white !important;
  }
  font-size: 10px;
  font-weight: 500;

  > img {
    height: 14px;
    margin-right: 8px;
  }
`;

const MenuList = styled.ul`
  margin: 18px 0;
  padding: 0;
  list-style: none;
  color: #111111;
`;

const AnchorItem = styled.a`
  min-width: 100%;
  margin: 0;
  padding: 5px 8px;
  padding-left: 36px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: #111111;
  &:hover,
  &:active,
  &:visited {
    color: #111111;
  }
`;

const ItemName = styled.span`
  position: relative;
`;

const NotificationDot = styled.div`
  position: absolute;
  top: 0;
  right: -8px;
  margin-left: 4px;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #bb0202;
`;

export type AccountMenuProps = {
  restrictedUser?: boolean;
  reactivate?: string;
  halfOffCoupon?: string;
  mentionsCount: number;
  messagesCount: number;
  hasUnseenReaders: boolean;
  notificationCount: number | null;
};

const AccountMenu = ({
  restrictedUser,
  reactivate,
  halfOffCoupon,
  mentionsCount,
  messagesCount,
  hasUnseenReaders,
  notificationCount,
}: AccountMenuProps) => {
  const upgradeAnchorUrl = reactivate
    ? routes.checkout_url({
        premium_discount_coupon: halfOffCoupon,
        from_navbar: true,
        trigger: TRIGGER,
      })
    : routes.upgrade_url({
        feature: 'default',
        from_navbar: true,
        trigger: TRIGGER,
      });

  const upgradeAnchorText = reactivate
    ? 'Reactivate Premium'
    : 'Upgrade to Premium';

  if (restrictedUser) {
    return (
      <MenuList>
        <li>
          <AnchorItem
            href={routes.settings_url({
              from_navbar: true,
              trigger: TRIGGER,
            })}
          >
            Account Settings
          </AnchorItem>
        </li>
        <li>
          <AnchorItem href={SUPPORT_LINK}>Help Center</AnchorItem>
        </li>
        <li>
          <AnchorItem
            href={routes.logout_url({
              from_navbar: true,
              trigger: TRIGGER,
            })}
          >
            Log Out
          </AnchorItem>
        </li>
      </MenuList>
    );
  }

  return (
    <>
      <Header href={currentUser?.url}>
        {currentUser != null && (
          <MenuProfilePic src={$image_url(currentUser.photo)} />
        )}
        <div>{currentUser?.display_name}</div>
      </Header>
      {!currentUser?.premium && (
        <ImpressionWatcher source={TRIGGER}>
          <UpgradeAnchor href={upgradeAnchorUrl}>
            <PremiumLock lockColor="white" />
            {upgradeAnchorText}
          </UpgradeAnchor>
        </ImpressionWatcher>
      )}
      <MenuList>
        <li>
          <AnchorItem href={currentUser?.url}>My Profile</AnchorItem>
        </li>
        <li>
          <AnchorItem
            href={routes.threads_url({
              from_navbar: true,
              trigger: TRIGGER,
            })}
          >
            My Messages
            <DoubleDigitCountBubble count={messagesCount} />
          </AnchorItem>
        </li>
        <li>
          <AnchorItem
            href={routes.notifications_url({
              from_navbar: true,
              trigger: TRIGGER,
            })}
          >
            Notifications
            <DoubleDigitCountBubble count={notificationCount} />
          </AnchorItem>
        </li>
        <li>
          <AnchorItem
            href={routes.reading_history_url({
              from_navbar: true,
              trigger: TRIGGER,
            })}
          >
            Reading History
          </AnchorItem>
        </li>
        <li>
          <AnchorItem
            href={routes.settings_url({
              from_navbar: true,
              trigger: TRIGGER,
            })}
          >
            Account Settings
          </AnchorItem>
        </li>
        <li>
          <AnchorItem
            href={routes.logout_url({
              from_navbar: true,
              trigger: TRIGGER,
            })}
          >
            Log Out
          </AnchorItem>
        </li>
      </MenuList>
      <Header>
        <img src={$image_url('header/focused/chart.svg')} />
        <div>My analytics</div>
      </Header>
      <MenuList>
        <li>
          <AnchorItem
            href={routes.mentions_url({
              from_navbar: true,
              trigger: TRIGGER,
            })}
          >
            Mentions
            {mentionsCount > 0 ? (
              <DoubleDigitCountBubble count={mentionsCount} />
            ) : (
              <PremiumLock />
            )}
          </AnchorItem>
        </li>
        <li>
          <AnchorItem
            href={routes.analytics_dashboard_url({
              page_name: currentUser?.page_name,
              subdomain: currentUser?.domain_name,
              from_navbar: true,
              trigger: TRIGGER,
            })}
          >
            {currentUser?.premium ? 'Analytics' : 'Basic Analytics'}
          </AnchorItem>
        </li>
        {currentUser?.premium ? null : (
          <li>
            <AnchorItem
              href={routes.upgrade_url({
                feature: 'analytics',
                from_navbar: true,
                trigger: TRIGGER,
              })}
            >
              Advanced Analytics
              <PremiumLock />
            </AnchorItem>
          </li>
        )}
        <li>
          <AnchorItem
            href={
              currentUser?.premium
                ? routes.analytics_dashboard_url({
                    page_name: currentUser?.page_name,
                    subdomain: currentUser?.domain_name,
                  }) +
                  '/activity/profile-visitors?from_navbar=true&trigger=' +
                  TRIGGER
                : routes.upgrade_url({
                    feature: 'profile_visitors',
                    from_navbar: true,
                    trigger: TRIGGER,
                  })
            }
          >
            Profile Visitors
            <PremiumLock />
          </AnchorItem>
        </li>
        <li>
          <AnchorItem
            href={
              currentUser?.premium
                ? routes.analytics_dashboard_url({
                    page_name: currentUser?.page_name,
                    subdomain: currentUser?.domain_name,
                  }) +
                  '/activity/readers?from_navbar=true&trigger=' +
                  TRIGGER
                : routes.upgrade_url({
                    feature: 'readers',
                    from_navbar: true,
                    trigger: TRIGGER,
                  })
            }
          >
            <ItemName>
              Readers
              {hasUnseenReaders && <NotificationDot />}
            </ItemName>
            <PremiumLock />
          </AnchorItem>
        </li>
      </MenuList>
      <Header>
        <img src={$image_url('header/focused/open_book.svg')} />
        <div>Academia.edu Journals</div>
      </Header>
      <MenuList>
        <li>
          <AnchorItem
            href={routes.academia_journals_url({
              from_navbar: true,
              trigger: TRIGGER,
              source: TRIGGER,
            })}
          >
            Journals Home
          </AnchorItem>
        </li>
        <li>
          <AnchorItem
            href={routes.academia_journals_articles_url({
              from_navbar: true,
              trigger: TRIGGER,
              source: TRIGGER,
            })}
          >
            All Articles
          </AnchorItem>
        </li>
        <li>
          <AnchorItem
            href={routes.journals_submissions_url({
              from_navbar: true,
              trigger: TRIGGER,
              source: TRIGGER,
            })}
          >
            Publishing Hub
          </AnchorItem>
        </li>
      </MenuList>
      <Header>
        <img src={$image_url('header/focused/nodes.svg')} />
        <div>Networking</div>
      </Header>
      <MenuList>
        <li>
          <AnchorItem
            href={routes.friend_finder_index_url({
              from_navbar: true,
              trigger: TRIGGER,
            })}
          >
            Find Colleagues
          </AnchorItem>
        </li>
        <li>
          <AnchorItem
            href={routes.s_url({
              from_navbar: true,
              trigger: TRIGGER,
            })}
          >
            Start a Discussion
          </AnchorItem>
        </li>
      </MenuList>
      <Header>
        <img src={$image_url('header/focused/more_horizontal.svg')} />
        <div>More</div>
      </Header>
      <MenuList>
        <li>
          <AnchorItem
            href={routes.community_standards_url({
              from_navbar: true,
              trigger: TRIGGER,
            })}
          >
            Community Standards
          </AnchorItem>
        </li>
        <li>
          <AnchorItem href={SUPPORT_LINK}>Help Center</AnchorItem>
        </li>
      </MenuList>
    </>
  );
};
export default AccountMenu;
